import { Component, HostListener, OnInit, computed, signal } from '@angular/core';
import { SurveyHeaderComponent } from '../../components/survey-header/survey-header.component';
import { BreadcrumbComponent } from '../../../../core/layout/components/breadcrumb/breadcrumb.component';
import { PageLoaderComponent } from '../../../../core/layout/components/page-loader/page-loader.component';
import { ApiSurveyDto, ApiSurveyModelService } from '../../../../shared/services/api-surveymodel.service';
import { ActivatedRoute } from '@angular/router';
import { forkJoin } from 'rxjs';
import { CanDeactivateComponent } from '../survey-builder/survey-builder.guard';
import { OrganizationStoreService } from '../../../../shared/services/organization-store.service';
import { UserStoreService } from '../../../../shared/services/user-store.service';
import { ApiPutSurveyTranslationsResponse, ApiSurveyTranslationsService } from '../../../../shared/services/api-surveytranslations.service';
import { SurveyTranslationsResponse } from '../../../../shared/models/surveytranslations-response.interface';
import { FormsModule } from '@angular/forms';
import { QuestionModel } from '../../models/question.model';
import { QuestionType } from '../../models/questionType.enum';
import { SupportedLanguage } from '../../../../shared/models/supportedLanguage.interface';

interface qTranView{
  questionCode: string;
  questionType: QuestionType;
  title: string|null;
  description: string|null;
  placeHolderLabel: string|null;
  choices: {choiceText:string, choiceValue:string}[];
  multiFields: {name:string, label:string}[] | null;
  questionOrder: number;
}

@Component({
  selector: 'app-survey-translations',
  templateUrl: './survey-translations.component.html',
  styleUrls: ['./survey-translations.component.css'],
  imports: [FormsModule, SurveyHeaderComponent, BreadcrumbComponent, PageLoaderComponent]
})
export class SurveyTranslationsComponent implements OnInit, CanDeactivateComponent {
  survey: ApiSurveyDto | null = null;
  questions: QuestionModel[] = [];
  translations: SurveyTranslationsResponse | null = null;
  loading = signal(false);
  error = signal<string | null>(null);
  defaultLanguage: string = 'en';
  previousLanguage: string | null = null;
  supportedLanguages: Array<{ lang: string } & SupportedLanguage> = [];
  questionTranslation: qTranView[] = [];
  private originalSnapshot = new Map<string, string>();
  saving = signal(false);
  saveError = signal<string | null>(null);
  showSaveResult = signal<{ success: number; failed: number } | null>(null);
  showSaveConfirm = signal(false);
  showDiscardConfirm = signal(false);
  showLeaveConfirm = signal(false);
  private leaveResolve: ((allow: boolean) => void) | null = null;
  showLanguageChangeConfirm = signal(false);

  readonly canEdit = computed(() => this.userStore.isAdmin());

  constructor(
    private route: ActivatedRoute,
    private organizationStore: OrganizationStoreService,
    private userStore: UserStoreService,
    private surveyModelService: ApiSurveyModelService,
    private surveyTranslationsService: ApiSurveyTranslationsService,) {}

  ngOnInit() {
    // surveySelectedGuard on the route guarantees these are present and valid.
    const organizationId = this.organizationStore.selectedOrganizationId()!;
    const surveyId = this.route.snapshot.paramMap.get('id')!;

    this.loadTranslationsModel(organizationId, surveyId);
  }

  private loadTranslationsModel(organizationId: string, surveyId: string): void {
    this.loading.set(true);
    this.error.set(null);

    forkJoin({
      survey: this.surveyModelService.getSurveyModel(organizationId, surveyId),
      translations: this.surveyTranslationsService.getSurveyTranslations(organizationId,surveyId)
    }).subscribe({
      next: ({ survey, translations }) => {
        // Survey Model
        this.survey = survey;
        this.questions = Object.entries(this.survey.questions).map(([key, value]) => ({
          ...value,
          questionCode: key,
        }));
        //Sort questions by questionOrder
        this.questions.sort((a, b) => a.questionOrder - b.questionOrder);

        // Set translations
        this.translations = translations;

        // Sort by culture / questionCode
        const questionOrder = new Map(this.questions.map((q, i) => [q.questionCode, i]));
        this.translations.translations.sort((a, b) => {
          const cultureCompare = a.culture.localeCompare(b.culture);
          if (cultureCompare !== 0) return cultureCompare;
          return (questionOrder.get(a.questionCode) ?? Infinity) - (questionOrder.get(b.questionCode) ?? Infinity);
        });

        if(this.previousLanguage){
          this.defaultLanguage = this.previousLanguage;
        }
        else {
          this.defaultLanguage = ['en', 'en-GB', 'en-US'].find(key => key in translations.supportedLanguages) ?? Object.keys(translations.supportedLanguages)[0];
          this.previousLanguage = this.defaultLanguage;
        }
        this.supportedLanguages = Object.entries(translations.supportedLanguages).map(([key, value]) => ({
          lang: key,
          ...value
        }));

        this.changeTranslations();
        this.loading.set(false);
      },
      error: (err) => {
        this.error.set('Failed to load survey translations. | ' + err);
        this.loading.set(false);
      }
    });
  }
  
  changeTranslations(): void {
    this.questionTranslation = [];
    for (const q of this.questions) {
      const cTran: {choiceText: string, choiceValue: string}[] = [];

      if (q.choices.length) {
        for (const c of q.choices) {
          cTran.push({
            choiceText: this.translations?.translations.find(t => t.questionCode == q.questionCode && t.choiceValue == c.choiceValue && t.culture == this.defaultLanguage)?.localizedText ?? '',
            choiceValue: c.choiceValue
          });
        }
      }

      this.questionTranslation.push({
        questionCode: q.questionCode,
        questionType: q.questionType,
        title: this.translations?.translations.find(t => t.questionCode == q.questionCode && t.choiceValue == null && t.culture == this.defaultLanguage)?.localizedText ?? '',
        description: null,
        placeHolderLabel: null,
        choices: cTran,
        multiFields: null,
        questionOrder: q.questionOrder
      });
    }
    this.takeSnapshot();
  }

  private takeSnapshot(): void {
    this.originalSnapshot.clear();
    for (const q of this.questionTranslation) {
      this.originalSnapshot.set(`${q.questionCode}|`, q.title ?? '');
      for (const c of q.choices) {
        this.originalSnapshot.set(`${q.questionCode}|${c.choiceValue}`, c.choiceText);
      }
    }
  }

  isTranslationChanged(questionCode: string, choiceValue: string | null): boolean {
    const key = `${questionCode}|${choiceValue ?? ''}`;
    const original = this.originalSnapshot.get(key);
    if (original === undefined) return false;
    const q = this.questionTranslation.find(q => q.questionCode === questionCode);
    if (!q) return false;
    const current = choiceValue === null
      ? (q.title ?? '')
      : (q.choices.find(c => c.choiceValue === choiceValue)?.choiceText ?? '');
    return current !== original;
  }

  get pendingChangesCount(): number {
    let count = 0;
    for (const q of this.questionTranslation) {
      if (this.originalSnapshot.get(`${q.questionCode}|`) !== (q.title ?? '')) count++;
      for (const c of q.choices) {
        if (this.originalSnapshot.get(`${q.questionCode}|${c.choiceValue}`) !== c.choiceText) count++;
      }
    }
    return count;
  }

  requestSave(): void {
    if (!this.canEdit()) return;
    this.saveError.set(null);
    this.showSaveResult.set(null);
    this.showSaveConfirm.set(true);
  }

  cancelSave(): void {
    if (this.saving()) return;
    this.saveError.set(null);
    this.showSaveResult.set(null);
    this.showSaveConfirm.set(false);
  }

  save(): void {
    if (!this.canEdit()) return;
    const organizationId = this.organizationStore.selectedOrganizationId();
    const surveyId = this.survey?.id;
    if (!organizationId || !surveyId) return;

    const changed: {questionCode: string, culture: string, choiceValue: string | null, localizedText: string}[] = [];
    for (const q of this.questionTranslation) {
      if (this.originalSnapshot.get(`${q.questionCode}|`) !== (q.title ?? '')) {
        changed.push({ questionCode: q.questionCode, culture: this.defaultLanguage, choiceValue: null, localizedText: q.title ?? '' });
      }
      for (const c of q.choices) {
        if (this.originalSnapshot.get(`${q.questionCode}|${c.choiceValue}`) !== c.choiceText) {
          changed.push({ questionCode: q.questionCode, culture: this.defaultLanguage, choiceValue: c.choiceValue, localizedText: c.choiceText });
        }
      }
    }

    if (changed.length === 0) return;

    this.saving.set(true);
    this.saveError.set(null);
    this.showSaveResult.set(null);
    this.surveyTranslationsService.updateSurveyTranslations(organizationId, surveyId, changed).subscribe({
      next: (response: ApiPutSurveyTranslationsResponse) => {
        this.saving.set(false);
        this.showSaveConfirm.set(false);
        const success = response.translations.filter(t => t.result.status === 'OK').length;
        const failed = response.translations.filter(t => t.result.status === 'KO').length;
        this.showSaveResult.set({ success, failed });
        this.loadTranslationsModel(organizationId, surveyId);
      },
      error: (err) => {
        this.saving.set(false);
        this.saveError.set(err?.message ?? 'Failed to save translations.');
      }
    });
  }

  requestDiscard(): void {
    if (!this.canEdit()) return;
    this.showDiscardConfirm.set(true);
  }

  cancelDiscard(): void {
    this.showDiscardConfirm.set(false);
  }

  discard(): void {
    if (!this.canEdit()) return;
    this.changeTranslations();
    this.showDiscardConfirm.set(false);
  }

  closeSaveResult(): void {
    this.showSaveResult.set(null);
  }

  requestLanguageChange(): void {
    if (this.pendingChangesCount === 0) {
      this.previousLanguage = this.defaultLanguage;
      this.changeTranslations();
      return;
    }
    this.showLanguageChangeConfirm.set(true);
  }

  confirmLanguageChange(): void {
    this.previousLanguage = this.defaultLanguage;
    this.changeTranslations();
    this.showLanguageChangeConfirm.set(false);
  }

  cancelLanguageChange(): void {
    this.defaultLanguage = this.previousLanguage ?? "en";
    this.showLanguageChangeConfirm.set(false);
  }

  setLeaveResolve(fn: (allow: boolean) => void): void {
    this.leaveResolve = fn;
  }

  confirmLeave(): void {
    this.showLeaveConfirm.set(false);
    this.leaveResolve?.(true);
    this.leaveResolve = null;
  }

  cancelLeave(): void {
    this.showLeaveConfirm.set(false);
    this.leaveResolve?.(false);
    this.leaveResolve = null;
  }

  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: BeforeUnloadEvent): void {
    if (this.pendingChangesCount > 0) {
      event.preventDefault();
    }
  }
}
